import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowUpDown, Lock } from "lucide-react";
import { fmtN, PaceArrow } from "./Momentum";
import { ProratedBadge } from "@/components/PlanBits";
import { TH, ALL_STAGES, StageCells, GroupHeads, SubHeads } from "./StageCells";
import { TdCell, TD_COL } from "./TdFleet";

const LEFT1 = "sticky left-0 z-[1] w-[180px] min-w-[180px] max-w-[180px] lg:w-[230px] lg:min-w-[230px] lg:max-w-[230px]";
const LEFT2 = "lg:sticky lg:left-[230px] lg:z-[1]";
const DESKTOP_DEFAULT = ["lead", "enquiry", "test_drive", "booking", "retail"];
const NARROW_DEFAULT = ["booking", "retail"];
const TD_IDX = ALL_STAGES.findIndex(([k]) => k === "test_drive");
const splitAtTd = (stages) => [stages.filter(([k]) => ALL_STAGES.findIndex(([s]) => s === k) <= TD_IDX), stages.filter(([k]) => ALL_STAGES.findIndex(([s]) => s === k) > TD_IDX)];

function AggRow({ r, label, stages, period, testId, cls, bg }) {
  const [before, after] = splitAtTd(stages);
  return (
    <tr className={`${cls} ${bg}`} data-testid={testId}>
      <td className={`px-2 py-1.5 ${LEFT1} ${bg} whitespace-nowrap overflow-hidden text-ellipsis`}>{label} <span className="text-muted-foreground font-normal text-[10px]">· {r.dealers} dealers · {r.approved} approved</span></td>
      <td className={`px-2 py-1.5 ${LEFT2} ${bg}`} />
      <td className="px-2 py-1.5" />
      <td className="px-2 py-1.5 text-right num">{fmtN(r.retail_projected)}</td>
      <td className="px-2 py-1.5 text-center"><PaceArrow m={r.momentum} compact /></td>
      <td className="px-2 py-1.5 text-right num">{fmtN(r.order_bank)}</td>
      {before.map(([k]) => <StageCells key={k} c={r.cells?.[k]} period={period} retail={k === "retail"} sticky={k === "retail"} bg={bg} testPrefix={`${testId}-${k}`} />)}
      <TdCell f={r.td_fleet} testId={`${testId}-td`} bg={bg} py="py-1.5" agg />
      {after.map(([k]) => <StageCells key={k} c={r.cells?.[k]} period={period} retail={k === "retail"} sticky={k === "retail"} bg={bg} testPrefix={`${testId}-${k}`} />)}
    </tr>
  );
}

export function DealerTable({ rows, regions, total, regionLabels, isManager, filtersNote, period }) {
  const [visible, setVisible] = useState(() => (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches ? DESKTOP_DEFAULT : NARROW_DEFAULT));
  const [sort, setSort] = useState({ key: "rank", dir: 1 });
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const h = (e) => setVisible(e.matches ? DESKTOP_DEFAULT : NARROW_DEFAULT);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  const stages = ALL_STAGES.filter(([k]) => visible.includes(k));
  const toggleGroup = (k) => setVisible((v) => (v.includes(k) ? (v.length > 1 ? v.filter((x) => x !== k) : v) : ALL_STAGES.map(([s]) => s).filter((s) => s === k || v.includes(s))));
  const regionMap = useMemo(() => Object.fromEntries(regions.map((r) => [r.label, r])), [regions]);
  const isWeek = period?.kind === "week";
  const projLabel = isWeek ? (period.current ? "Proj. week-end" : "Proj.") : "Retail proj.";
  const paceLabel = isWeek ? "DoD" : "7-day";

  const sorter = (a, b) => {
    const v = (r) => {
      if (sort.key === "rank") return r.rank == null ? 9999 : r.rank;
      if (sort.key === "dealer") return r.dealer_code;
      if (sort.key === "projected") return r.retail_projected ?? -1;
      if (sort.key === "order_bank") return r.order_bank ?? -1e9;
      if (sort.key === "momentum") return r.momentum?.change ?? -1e9;
      if (sort.key === "td") return r.td_fleet?.per_car_day ?? -1;
      const [k, f] = sort.key.split(".");
      return r.cells?.[k]?.[f] ?? -1;
    };
    const x = v(a), y = v(b);
    return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
  };
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === "rank" || key === "dealer" ? 1 : -1 }));
  const Th = ({ k, children, className = "", ...rest }) => (
    <th className={`${TH} ${className} cursor-pointer select-none hover:text-foreground`} onClick={() => toggleSort(k)} data-testid={`ov-sort-${k.replace(".", "-")}`} {...rest}>
      <span className="inline-flex items-center gap-0.5">{children}{sort.key === k && <ArrowUpDown size={10} />}</span>
    </th>
  );

  const groups = (regionLabels.length ? regionLabels : ["All"]).map((label) => ({ label, rows: rows.filter((r) => regionLabels.length === 0 || r.region === label).sort(sorter) })).filter((g) => g.rows.length);
  const colCount = 7 + stages.length * 4;
  const [planLbl, tdLbl] = isWeek ? ["Week Plan", "WTD Plan"] : ["Month Plan", "MTD Plan"];
  const [before, after] = splitAtTd(stages);

  return (
    <div className="border bg-card rounded-md" data-testid="ov-dealer-table">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">C · Dealerships <span className="text-muted-foreground font-normal text-xs">· {planLbl} / {tdLbl} / Ach / % (Ach ÷ {tdLbl}) · ranked on Retail % · TDs/car/day = test drives ÷ TD cars ÷ working days{filtersNote}</span></h3>
        <div className="ml-auto flex flex-wrap items-center gap-1" data-testid="ov-group-chips">
          {ALL_STAGES.map(([k, l]) => (
            <button key={k} onClick={() => toggleGroup(k)} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${visible.includes(k) ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:bg-secondary"}`} aria-pressed={visible.includes(k)} data-testid={`ov-group-chip-${k}`}>{l}</button>
          ))}
        </div>
        {isManager && <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Lock size={11} /> other dealers show rank only</span>}
      </div>
      <div className="overflow-auto max-h-[560px]">
        <table className="w-full text-xs" style={{ minWidth: 610 + stages.length * 288 }}>
          <thead className="border-b sticky top-0 bg-card z-[2]">
            <tr>
              <Th k="dealer" className={`text-left ${LEFT1} bg-card z-[3]`}>Dealer</Th>
              <th className={`${TH} ${LEFT2} bg-card`}>Region</th>
              <Th k="rank" className="text-right">Rank</Th>
              <Th k="projected" className="text-right">{projLabel}</Th>
              <Th k="momentum" className="text-center">{paceLabel}</Th>
              <Th k="order_bank" className="text-right">Order bank</Th>
              <GroupHeads stages={before} freezeRetail testPrefix="ov-dealer" />
              <Th k="td" className={`text-right align-middle ${TD_COL}`} rowSpan={2} title="Test drives ÷ TD cars ÷ working days (elapsed) · hover a value for the TD cars">TDs/car/day</Th>
              <GroupHeads stages={after} freezeRetail testPrefix="ov-dealer" />
            </tr>
            <tr className="text-[9px] text-muted-foreground">
              <th className={`${LEFT1} bg-card`} /><th className={`${LEFT2} bg-card`} /><th /><th /><th /><th />
              <SubHeads stages={before} period={period} freezeRetail />
              <SubHeads stages={after} period={period} freezeRetail />
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const sub = regionMap[g.label];
              const isCollapsed = !!collapsed[g.label];
              return [
                regionLabels.length > 0 && !isManager && (
                  <tr key={`${g.label}-head`} className="bg-secondary/60 cursor-pointer hover:bg-secondary" onClick={() => setCollapsed((c) => ({ ...c, [g.label]: !c[g.label] }))} data-testid={`ov-region-head-${g.label.replace(/\s+/g, "_")}`}>
                    <td className="px-2 py-1 font-semibold text-[11px] whitespace-nowrap" colSpan={colCount}><span className="inline-flex items-center gap-1 sticky left-2">{isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />} {g.label} <span className="text-muted-foreground font-normal">· {g.rows.length} dealers</span></span></td>
                  </tr>
                ),
                !isCollapsed && g.rows.map((r) => (
                  <tr key={r.dealer_code} className={`border-b last:border-0 bg-card hover:bg-muted/50 ${r.masked ? "text-muted-foreground" : ""}`} data-testid={`ov-dealer-${r.dealer_code}`}>
                    <td className={`px-2 py-1 ${LEFT1} bg-card whitespace-nowrap overflow-hidden text-ellipsis`}>
                      {r.masked ? <span className="inline-flex items-center gap-1"><Lock size={10} /> {r.dealer_code} <span className="text-[10px]">{r.dealer_name}</span></span> : (
                        <Link to={`/dpc/dealer/${r.dealer_code}`} className="font-medium hover:underline" data-testid={`ov-dealer-link-${r.dealer_code}`}>{r.dealer_code} <span className="text-muted-foreground font-normal">{r.dealer_name}</span></Link>
                      )}
                      {!r.masked && !r.approved && <span className="ml-1.5 rounded bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5 text-[9px]" data-testid={`ov-dealer-noplan-${r.dealer_code}`}>no approved plan</span>}
                      {!r.masked && r.prorated && <ProratedBadge count={1} className="ml-1.5" testId={`ov-dealer-prorated-${r.dealer_code}`} />}
                      {!r.masked && r.uploaded_asof === false && <span className="ml-1 rounded bg-red-50 text-red-700 px-1 py-0.5 text-[9px]">no upload</span>}
                    </td>
                    <td className={`px-2 py-1 ${LEFT2} bg-card text-muted-foreground whitespace-nowrap text-[10px]`}>{r.region}</td>
                    <td className="px-2 py-1 text-right num font-bold" data-testid={`ov-dealer-${r.dealer_code}-rank`}>{r.rank ?? "—"}</td>
                    {r.masked ? <td className="px-2 py-1 text-center text-[10px] text-muted-foreground" colSpan={4 + stages.length * 4}>— masked · rank only —</td> : (
                      <>
                        <td className="px-2 py-1 text-right num" data-testid={`ov-dealer-${r.dealer_code}-proj`}>{fmtN(r.retail_projected)}</td>
                        <td className="px-2 py-1 text-center"><PaceArrow m={r.momentum} compact testId={`ov-dealer-${r.dealer_code}-pace`} /></td>
                        <td className="px-2 py-1 text-right num" data-testid={`ov-dealer-${r.dealer_code}-ob`}>{fmtN(r.order_bank)}</td>
                        {before.map(([k]) => <StageCells key={k} c={r.cells?.[k]} period={period} retail={k === "retail"} sticky={k === "retail"} testPrefix={`ov-dealer-${r.dealer_code}-${k}`} />)}
                        <TdCell f={r.td_fleet} testId={`ov-dealer-${r.dealer_code}-td`} />
                        {after.map(([k]) => <StageCells key={k} c={r.cells?.[k]} period={period} retail={k === "retail"} sticky={k === "retail"} testPrefix={`ov-dealer-${r.dealer_code}-${k}`} />)}
                      </>
                    )}
                  </tr>
                )),
                !isManager && sub && regionLabels.length > 1 && <AggRow key={`${g.label}-sub`} r={sub} label={`${g.label} subtotal`} stages={stages} period={period} testId={`ov-region-${g.label.replace(/\s+/g, "_")}`} cls="font-semibold border-b" bg="bg-secondary" />,
              ];
            })}
            {total && <AggRow r={total} label={total.label} stages={stages} period={period} testId="ov-total" cls="font-bold border-t-2 border-foreground/30" bg="bg-secondary" />}
            {rows.length === 0 && <tr><td colSpan={colCount} className="px-3 py-6 text-center text-muted-foreground">No dealers in this selection</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
